<!-- Trigger -->
<!--
	The weight and case sit on a span inside the label: `<eff-button>` renders its own `<button>`,
	which does not inherit `font-weight` from the host, and the kit exposes no token for it.
-->
<eff-button
	size="md"
	class="ds-user-menu__trigger"
	[matMenuTriggerFor]="menu"
	[ariaLabel]="triggerLabel()"
	matTooltip="User Details"
	matTooltipPosition="right"
	dsTestId="shell-user-menu-trigger"
>
	<span class="ds-h-text-weight--bold ds-h-text-transform--uppercase">{{ initials() }}</span>
</eff-button>
<!-- / Trigger -->

<!--
	`yPosition="above"` + `xPosition="after"` is the rail-footer anchoring: the trigger sits at the
	bottom-left of the viewport, so the panel opens upward and to the right of it.
-->
<mat-menu
	#menu="matMenu"
	class="ds-menu-panel ds-menu-panel--dark ds-user-menu__panel"
	xPosition="after"
	yPosition="above"
>
	<ng-template matMenuContent>
		<!-- Identity -->
		<div vertical="center" class="ds-flex eff-gap--sm ds-user-menu__identity">
			<!-- aria-hidden: the initials say nothing the name beside them does not say better. -->
			<span aria-hidden="true" class="ds-user-menu__avatar">{{ initials() }}</span>

			<div class="ds-user-menu__identity-text">
				<p
					showTooltipIfTruncated
					class="slds-truncate ds-text--medium ds-h-text-weight--bold ds-user-menu__identity-name"
					[matTooltip]="displayName()"
					dsTestId="shell-user-menu-name"
				>
					{{ displayName() }}
				</p>
				@if (email(); as email) {
					<!--
						Two spans, because an address too long for the row loses its MIDDLE rather than its
						end: the local part shrinks with an ellipsis while the domain stays whole, so what
						survives is the half that identifies the account. CSS has no middle ellipsis, and
						the head/tail split is the whole mechanism — see the stylesheet.
					-->
					<p class="ds-text--small ds-user-menu__identity-email" dsTestId="shell-user-menu-email">
						<span
							showTooltipIfTruncated
							class="slds-truncate ds-user-menu__email-head"
							[matTooltip]="email"
							>{{ emailHead() }}</span
						><span class="ds-user-menu__email-tail">{{ emailTail() }}</span>
					</p>
				}
			</div>
		</div>
		<!-- / Identity -->

		<!-- Status & CTAs -->
		@if (isStatusGroupVisible()) {
			<mat-divider class="ds-user-menu__divider" />
		}

		@if (isSupportBannerVisible()) {
			<p class="ds-text--small ds-user-menu__support-banner" dsTestId="shell-user-menu-support-banner">
				Logged in as:
				<strong>
					{{ displayName() }}
					@if (accountName(); as accountName) {
						({{ accountName }})
					}
				</strong>
			</p>
		}

		@if (isUpgradeEnabled) {
			<button type="button" mat-menu-item dsTestId="shell-user-menu-upgrade" (click)="openUpgradePlanDialog()">
				Upgrade
			</button>
		}

		@if (isTrialCtaVisible()) {
			<button type="button" mat-menu-item dsTestId="shell-user-menu-trial-cta" (click)="openBilling()">
				Trial version — <strong>Activate Now</strong>
			</button>
		}

		@if (commitId) {
			<a
				mat-menu-item
				class="ds-h-text-transform--uppercase"
				[href]="commitUrl"
				target="_blank"
				rel="noopener noreferrer"
				dsTestId="shell-user-menu-revision"
				>{{ shortCommitId }}</a
			>
		}

		<!-- / Status & CTAs -->

		<mat-divider class="ds-user-menu__divider" />
		<p class="ds-text--x-small ds-user-menu__group-title">GENERAL</p>

		<a
			mat-menu-item
			[routerLink]="scheduleRoute"
			queryParamsHandling="merge"
			[queryParams]="emptyQuery"
			dsTestId="shell-user-menu-schedule"
			>Schedule</a
		>

		<!-- Brain's order (`UserProfileDropdown.tsx:186-196`): MCP Tools directly before Integrations. -->
		<a
			mat-menu-item
			[routerLink]="mcpSettingsRoute"
			queryParamsHandling="merge"
			[queryParams]="emptyQuery"
			dsTestId="shell-user-menu-mcp-tools"
			>MCP Tools</a
		>

		<a
			mat-menu-item
			[routerLink]="integrationsRoute"
			queryParamsHandling="merge"
			[queryParams]="emptyQuery"
			dsTestId="shell-user-menu-integrations"
			>Integrations</a
		>

		<!-- Brain's order (`UserProfileDropdown.tsx:211-224`): Organization between Integrations and Admin Panel. -->
		@if (isOrganizationVisible()) {
			<a
				mat-menu-item
				[routerLink]="organizationRoute"
				queryParamsHandling="merge"
				[queryParams]="emptyQuery"
				dsTestId="shell-user-menu-organization"
				>Organization</a
			>
		}

		@if (isClarityAdminVisible()) {
			<a
				mat-menu-item
				[routerLink]="clarityAdminRoute"
				queryParamsHandling="merge"
				[queryParams]="emptyQuery"
				dsTestId="shell-user-menu-clarity-admin"
				>Admin Panel</a
			>
		}

		<!-- Brain's order (`UserProfileDropdown.tsx:234-259`): Admin Panel, then System Domain, last in GENERAL. -->
		@if (systemDomainEntry(); as entry) {
			<button
				type="button"
				mat-menu-item
				dsTestId="shell-user-menu-system-domain"
				(click)="openSystemDomain(entry.systemDomainId)"
			>
				System Domain
			</button>
		}

		<!-- Org -->
		@if (isOrgGroupVisible()) {
			<mat-divider class="ds-user-menu__divider" />
			<p class="ds-text--x-small ds-user-menu__group-title">ORG</p>

			@if (isAccountsAndContactsVisible()) {
				<a
					mat-menu-item
					[routerLink]="accountsAndContactsRoute"
					queryParamsHandling="merge"
					[queryParams]="emptyQuery"
					dsTestId="shell-user-menu-accounts-and-contacts"
					>{{ accountsAndContactsLabel }}</a
				>
			}
			@if (isAdminConsoleVisible()) {
				<a
					mat-menu-item
					[routerLink]="adminConsoleRoute"
					queryParamsHandling="merge"
					[queryParams]="emptyQuery"
					dsTestId="shell-user-menu-admin-console"
					(click)="trackAdminConsole()"
					>{{ adminConsoleLabel }}</a
				>
			}
		}
		<!-- / Org -->

		<!-- Profile -->
		<mat-divider class="ds-user-menu__divider" />
		<p class="ds-text--x-small ds-user-menu__group-title">PROFILE</p>

		<button type="button" mat-menu-item dsTestId="shell-user-menu-my-profile" (click)="openMyProfile()">
			My Profile
		</button>
		<button type="button" mat-menu-item dsTestId="shell-user-menu-my-settings" (click)="openMySettings()">
			My Settings
		</button>
		@if (isBillingEnabled()) {
			<button type="button" mat-menu-item dsTestId="shell-user-menu-billing" (click)="openBillingFromMenu()">
				Billing
			</button>
		}
		<!--
			One entry for four pages, and the last row of `PROFILE` rather than a group of its own: the
			section is one destination, and a heading over a single row reads as a category that is
			missing its other entries. The sub-tabs live in the shell's own strip, which the section's
			host registers when the group is entered (EFF-24841). The link is the first page this caller
			may open rather than a fixed landing, so a reader who may only see Organizations lands there
			instead of bouncing off Catalog's route guard.
		-->
		@if (isPlatformAdminVisible()) {
			<a
				mat-menu-item
				[routerLink]="platformAdminRoute()"
				queryParamsHandling="merge"
				[queryParams]="emptyQuery"
				dsTestId="shell-user-menu-platform-administrator"
				>{{ platformAdminLabel }}</a
			>
		}
		<!-- / Profile -->

		<!--
			DEMO ONLY — DEVOPS-746. Gated on the flags-operator permission, not on the flag: the panel's
			own point is what happens while the flag is OFF. `isDemoEnabled` also carries the sandbox
			check, so the entry is absent on production for any role.
		-->
		@if (isDemoEnabled) {
			<mat-divider class="ds-user-menu__divider" />
			<!-- Variant 1: a whole surface, opened from a button. The prototype is fetched on click. -->
			<button
				type="button"
				mat-menu-item
				dsTestId="shell-user-menu-feature-flags-demo"
				(click)="openFeatureFlagsDemo()"
			>
				Feature-flag demo
			</button>
		}

		<!--
			Variant 2: a fragment of THIS template that belongs to the prototype. `ngComponentOutlet`
			takes the component as a value, so this template never names the prototype — a tag here
			would be a static reference from the initial bundle, which is what shipping it to every user
			looks like. The host fetches it when the flag turns on and drops it when the flag turns off.
			No `isDemoEnabled` here on purpose: that gate sits in `syncGatedFragment`, so an ineligible
			caller never fetches the chunk.
		-->
		@if (gatedFragment(); as fragment) {
			<ng-container *ngComponentOutlet="fragment" />
		}

		<!-- Log Out -->
		<mat-divider class="ds-user-menu__divider" />
		<button type="button" mat-menu-item dsTestId="shell-user-menu-logout" (click)="logout()">
			<eff-icon matMenuItemIcon name="log-out" [size]="18" />
			<span>Log Out</span>
		</button>
		<!-- / Log Out -->
	</ng-template>
</mat-menu>
